import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import LoginModal from './LoginModal';
import TopBarNav from '../styled/Topbar';
import { useTranslation } from "react-i18next";
import { useChat } from "../context/ChatProvider";
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { BeatLoader } from 'react-spinners';
import { 
  MdTranslate, 
  MdChat, 
  MdSmartToy, 
  MdGroups, 
  MdArrowForward,
  MdVolumeUp,
  MdLanguage
} from "react-icons/md";

/* ─── animations ─── */

const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(30px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const float = keyframes`
  0%, 100% { transform: translateY(0); }
  50%      { transform: translateY(-12px); }
`;

const gradientShift = keyframes`
  0%   { background-position: 0% 50%; }
  50%  { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

const pulse = keyframes`
  0%, 100% { opacity: 0.4; }
  50%      { opacity: 0.8; }
`;

/* ─── layout ─── */

const PageWrapper = styled.div`
  min-height: 100vh;
  overflow-x: hidden;
  background: linear-gradient(135deg, #0f0c29 0%, #302b63 50%, #24243e 100%);
  background-size: 200% 200%;
  animation: ${gradientShift} 15s ease infinite;
`;

const HeroSection = styled.section`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  padding: 8rem 2rem 4rem;
  text-align: center;
  overflow: hidden;
`;

/* floating decorative orbs */
const Orb = styled.div`
  position: absolute;
  border-radius: 50%;
  filter: blur(80px);
  pointer-events: none;
  animation: ${pulse} 6s ease-in-out infinite;

  &:nth-child(1) {
    width: 500px; height: 500px;
    background: rgba(99, 102, 241, 0.25);
    top: -10%; left: -10%;
    animation-delay: 0s;
  }
  &:nth-child(2) {
    width: 400px; height: 400px;
    background: rgba(236, 72, 153, 0.20);
    bottom: -5%; right: -8%;
    animation-delay: 2s;
  }
  &:nth-child(3) {
    width: 300px; height: 300px;
    background: rgba(52, 211, 153, 0.15);
    top: 40%; left: 60%;
    animation-delay: 4s;
  }
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 2;
  max-width: 820px;
  animation: ${fadeInUp} 0.8s ease-out;
`;

const LogoRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.6rem;
  margin-bottom: 1.6rem;
`;

const LogoImage = styled.img`
  width: 56px;
  height: 56px;
  border-radius: 50%;
  border: 2px solid rgba(255,255,255,0.3);
  animation: ${float} 4s ease-in-out infinite;
`;

const LogoText = styled.h1`
  font-size: clamp(2rem, 5vw, 3.2rem);
  font-weight: 800;
  background: linear-gradient(135deg, #ffffff 0%, #a5b4fc 50%, #f0abfc 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  letter-spacing: -0.02em;
`;

const Tagline = styled.p`
  font-size: clamp(1.1rem, 2.5vw, 1.5rem);
  color: rgba(255, 255, 255, 0.85);
  line-height: 1.6;
  margin-bottom: 0.5rem;
  font-weight: 300;
`;

const SubTagline = styled.p`
  font-size: clamp(0.95rem, 2vw, 1.15rem);
  color: rgba(255, 255, 255, 0.55);
  line-height: 1.6;
  margin-bottom: 2.5rem;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
`;

/* ─── CTA button ─── */

const CTAButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.6rem;
  padding: 1rem 2.4rem;
  border: none;
  border-radius: 60px;
  background: linear-gradient(135deg, #6366f1, #8b5cf6, #a855f7);
  color: white;
  font-size: 1.15rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 24px rgba(99, 102, 241, 0.4);
  min-width: 220px;
  justify-content: center;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 32px rgba(99, 102, 241, 0.55);
  }

  &:active {
    transform: translateY(0);
  }
`;

const SecondaryLink = styled.span`
  display: inline-block;
  margin-top: 1.2rem;
  color: rgba(255, 255, 255, 0.5);
  font-size: 0.9rem;
  cursor: pointer;
  transition: color 0.2s;

  &:hover {
    color: rgba(255, 255, 255, 0.85);
  }
`;

/* ─── language ticker ─── */

const tickerScroll = keyframes`
  0%   { transform: translateX(0); }
  100% { transform: translateX(-50%); }
`;

const TickerWrapper = styled.div`
  width: 100%;
  overflow: hidden;
  padding: 1.5rem 0;
  margin-top: 3rem;
  position: relative;
  z-index: 2;

  &::before, &::after {
    content: '';
    position: absolute;
    top: 0; bottom: 0;
    width: 80px;
    z-index: 3;
    pointer-events: none;
  }
  &::before { left: 0;  background: linear-gradient(to right, #0f0c29, transparent); }
  &::after  { right: 0; background: linear-gradient(to left, #24243e, transparent); }
`;

const TickerTrack = styled.div`
  display: flex;
  gap: 2rem;
  animation: ${tickerScroll} 30s linear infinite;
  width: max-content;
`;

const TickerItem = styled.span`
  white-space: nowrap;
  font-size: 0.95rem;
  color: rgba(255, 255, 255, 0.35);
  font-weight: 500;
  letter-spacing: 0.03em;
`;

/* ─── features section ─── */

const FeaturesSection = styled.section`
  padding: 6rem 2rem;
  max-width: 1100px;
  margin: 0 auto;
`;

const SectionLabel = styled.p`
  text-align: center;
  text-transform: uppercase;
  letter-spacing: 0.15em;
  font-size: 0.8rem;
  color: #a78bfa;
  font-weight: 600;
  margin-bottom: 0.8rem;
`;

const SectionTitle = styled.h2`
  text-align: center;
  font-size: clamp(1.6rem, 4vw, 2.4rem);
  font-weight: 700;
  color: white;
  margin-bottom: 3.5rem;
`;

const FeatureGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1.8rem;
`;

const appear = keyframes`
  from { opacity: 0; transform: translateY(24px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const FeatureCard = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 20px;
  padding: 2rem 1.8rem;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);

  animation: ${appear} 0.6s ease-out both;
  animation-delay: ${props => props.$delay || '0s'};

  &:hover {
    background: rgba(255, 255, 255, 0.08);
    border-color: rgba(255, 255, 255, 0.15);
    transform: translateY(-4px);
  }
`;

const FeatureIconWrapper = styled.div`
  width: 52px;
  height: 52px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1.2rem;
  background: ${props => props.$bg || 'rgba(99,102,241,0.2)'};
  color: ${props => props.$color || '#a5b4fc'};
  font-size: 1.5rem;
`;

const FeatureTitle = styled.h3`
  font-size: 1.15rem;
  font-weight: 600;
  color: white;
  margin-bottom: 0.5rem;
`;

const FeatureDesc = styled.p`
  font-size: 0.92rem;
  color: rgba(255, 255, 255, 0.55);
  line-height: 1.6;
`;

/* ─── demo preview ─── */

const DemoSection = styled.section`
  padding: 4rem 2rem 6rem;
  max-width: 700px;
  margin: 0 auto;
  animation: ${fadeInUp} 0.8s ease-out;
`;

const DemoChatWindow = styled.div`
  background: rgba(255,255,255,0.04);
  border: 1px solid rgba(255,255,255,0.1);
  border-radius: 24px;
  padding: 2rem;
  backdrop-filter: blur(10px);
`;

const DemoBubble = styled.div`
  display: flex;
  flex-direction: column;
  align-items: ${props => props.$align === 'right' ? 'flex-end' : 'flex-start'};
  margin-bottom: 1.2rem;

  &:last-child { margin-bottom: 0; }
`;

const BubbleName = styled.span`
  font-size: 0.75rem;
  color: ${props => props.$color || 'rgba(255,255,255,0.4)'};
  margin-bottom: 0.3rem;
  font-weight: 500;
`;

const BubbleContent = styled.div`
  background: ${props => props.$bg || 'rgba(99,102,241,0.25)'};
  border-radius: 16px;
  padding: 0.8rem 1.2rem;
  max-width: 75%;
`;

const BubbleText = styled.p`
  color: rgba(255,255,255,0.9);
  font-size: 0.95rem;
  line-height: 1.5;
`;

const BubbleTranslation = styled.p`
  color: rgba(255,255,255,0.45);
  font-size: 0.8rem;
  margin-top: 0.3rem;
  font-style: italic;
`;

/* ─── footer ─── */

const Footer = styled.footer`
  text-align: center;
  padding: 3rem 2rem;
  border-top: 1px solid rgba(255,255,255,0.06);
`;

const FooterText = styled.p`
  color: rgba(255,255,255,0.3);
  font-size: 0.85rem;
`;

const FooterLinks = styled.div`
  display: flex;
  justify-content: center;
  gap: 1.5rem;
  margin-bottom: 1rem;
`;

const FooterLink = styled.span`
  color: rgba(255,255,255,0.4);
  font-size: 0.85rem;
  cursor: pointer;
  transition: color 0.2s;

  &:hover { color: rgba(255,255,255,0.7); }
`;

/* ─── ticker languages ─── */

const LANGUAGES = [
  'English', '日本語', 'Español', 'Français', '한국어', 'Deutsch',
  'Português', 'العربية', 'हिन्दी', '中文', 'ภาษาไทย', 'Nederlands',
  'Magyar', 'Русский', 'বাংলা', 'Italiano', 'Tiếng Việt', 'Filipino',
];

/* ─── component ─── */

const Homepage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);
  const { setUser, setToken, setCurrentRoom, setInitJoin, setActiveView, setIsNavOpen, resetContextFor } = useChat();
  const [startClicked, setStartClicked] = useState(false);

  // scroll to top on mount
  useEffect(() => { window.scrollTo(0, 0); }, []);

  const handleBabbleClick = async () => {
    setStartClicked(true);
    try {
      const response = await axios.get(`${process.env.REACT_APP_SERVER}/init-chat`, { withCredentials: true });
      const { room, user, token } = response.data;

      setToken(token);
      resetContextFor(user);

      sessionStorage.setItem('user', JSON.stringify(user));
      sessionStorage.setItem('userLoggedInWithGoogle', "FALSE");

      setCurrentRoom(room);
      sessionStorage.setItem('currentRoomId', room.id);
      sessionStorage.setItem('currentRoom', JSON.stringify(room));

      setInitJoin(room.id);
      setActiveView("chat");
      setIsNavOpen(false);

      navigate('/init-chat');
    } catch (error) {
      console.log(error);
      setStartClicked(false);
    }
  };

  const tickerItems = [...LANGUAGES, ...LANGUAGES]; // duplicate for seamless loop

  return (
    <PageWrapper>
      <TopBarNav onSignInClick={() => setShowModal(true)} />

      {/* ── Hero ── */}
      <HeroSection>
        <Orb /><Orb /><Orb />

        <HeroContent>
          <LogoRow>
            <LogoImage src="/babblers/BabbleonLogo.png" alt="BabbleOn" />
            <LogoText>BabbleOn</LogoText>
          </LogoRow>

          <Tagline>{t('HomepagePhrase')}</Tagline>
          <SubTagline>{t('HomepagePhrase2')}</SubTagline>

          <CTAButton onClick={handleBabbleClick}>
            {startClicked
              ? <BeatLoader size={10} color="white" />
              : <>{t('SignupButton')} <MdArrowForward size={20} /></>}
          </CTAButton>

          <br />
          <SecondaryLink onClick={() => navigate('/signup')}>
            {t('DontHaveAccount') || "Don't have an account?"} →
          </SecondaryLink>
        </HeroContent>

        {/* Language ticker */}
        <TickerWrapper>
          <TickerTrack>
            {tickerItems.map((lang, i) => (
              <TickerItem key={i}>● {lang}</TickerItem>
            ))}
          </TickerTrack>
        </TickerWrapper>
      </HeroSection>

      {/* ── Features ── */}
      <FeaturesSection>
        <SectionLabel>Features</SectionLabel>
        <SectionTitle>Everything you need to chat across languages</SectionTitle>

        <FeatureGrid>
          <FeatureCard $delay="0s">
            <FeatureIconWrapper $bg="rgba(99,102,241,0.2)" $color="#a5b4fc">
              <MdTranslate />
            </FeatureIconWrapper>
            <FeatureTitle>Instant Translation</FeatureTitle>
            <FeatureDesc>
              Every message is translated in real-time to each participant's preferred language, powered by AI.
            </FeatureDesc>
          </FeatureCard>

          <FeatureCard $delay="0.1s">
            <FeatureIconWrapper $bg="rgba(236,72,153,0.2)" $color="#f9a8d4">
              <MdChat />
            </FeatureIconWrapper>
            <FeatureTitle>Real-Time Chat</FeatureTitle>
            <FeatureDesc>
              Live messaging with instant updates. Create rooms, invite friends, and start conversations in seconds.
            </FeatureDesc>
          </FeatureCard>

          <FeatureCard $delay="0.2s">
            <FeatureIconWrapper $bg="rgba(52,211,153,0.2)" $color="#6ee7b7">
              <MdSmartToy />
            </FeatureIconWrapper>
            <FeatureTitle>AI Language Partners</FeatureTitle>
            <FeatureDesc>
              Practice with AI chatbot "Babblers" that converse naturally in any language you want to learn.
            </FeatureDesc>
          </FeatureCard>

          <FeatureCard $delay="0.3s">
            <FeatureIconWrapper $bg="rgba(251,191,36,0.2)" $color="#fcd34d">
              <MdGroups />
            </FeatureIconWrapper>
            <FeatureTitle>Guest Invites</FeatureTitle>
            <FeatureDesc>
              Share a link to invite anyone—no account needed. They pick a language and join instantly.
            </FeatureDesc>
          </FeatureCard>

          <FeatureCard $delay="0.4s">
            <FeatureIconWrapper $bg="rgba(239,68,68,0.2)" $color="#fca5a5">
              <MdVolumeUp />
            </FeatureIconWrapper>
            <FeatureTitle>Text-to-Speech</FeatureTitle>
            <FeatureDesc>
              Hear messages spoken aloud with natural-sounding voices to improve your pronunciation.
            </FeatureDesc>
          </FeatureCard>

          <FeatureCard $delay="0.5s">
            <FeatureIconWrapper $bg="rgba(14,165,233,0.2)" $color="#7dd3fc">
              <MdLanguage />
            </FeatureIconWrapper>
            <FeatureTitle>18+ Languages</FeatureTitle>
            <FeatureDesc>
              From Japanese to Spanish, Portuguese to Korean—communicate with people across the globe.
            </FeatureDesc>
          </FeatureCard>
        </FeatureGrid>
      </FeaturesSection>

      {/* ── Demo preview ── */}
      <DemoSection>
        <SectionLabel>See it in action</SectionLabel>
        <SectionTitle>One conversation, every language</SectionTitle>

        <DemoChatWindow>
          <DemoBubble $align="left">
            <BubbleName $color="#a5b4fc">Kenji 🇯🇵</BubbleName>
            <BubbleContent $bg="rgba(99,102,241,0.2)">
              <BubbleText>今日の天気はとても良いですね！</BubbleText>
              <BubbleTranslation>The weather is really nice today!</BubbleTranslation>
            </BubbleContent>
          </DemoBubble>

          <DemoBubble $align="right">
            <BubbleName $color="#f9a8d4">María 🇪🇸</BubbleName>
            <BubbleContent $bg="rgba(236,72,153,0.18)">
              <BubbleText>¡Sí! Es un día perfecto para pasear.</BubbleText>
              <BubbleTranslation>Yes! It's a perfect day for a walk.</BubbleTranslation>
            </BubbleContent>
          </DemoBubble>

          <DemoBubble $align="left">
            <BubbleName $color="#6ee7b7">Lucas 🇧🇷</BubbleName>
            <BubbleContent $bg="rgba(52,211,153,0.18)">
              <BubbleText>Concordo! Vamos aproveitar o sol.</BubbleText>
              <BubbleTranslation>I agree! Let's enjoy the sun.</BubbleTranslation>
            </BubbleContent>
          </DemoBubble>
        </DemoChatWindow>
      </DemoSection>

      {/* ── Footer ── */}
      <Footer>
        <FooterLinks>
          <FooterLink onClick={() => navigate('/about-us')}>{t('TopBarAboutUs')}</FooterLink>
          <FooterLink onClick={() => navigate('/privacy')}>{t('Privacy')}</FooterLink>
          <FooterLink onClick={() => navigate('/terms')}>Terms</FooterLink>
          <FooterLink 
            href="https://labs.babbleon.ai"
            target="_blank"
            rel="noopener noreferrer">Built in labs.babbleon.ai
          </FooterLink>
        </FooterLinks>
        <FooterText>© {new Date().getFullYear()} BabbleOn. All rights reserved.</FooterText>
      </Footer>

      {showModal && <LoginModal onClose={() => setShowModal(false)} />}
    </PageWrapper>
  );
};

export default Homepage;
